import React, { useState, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Collapse,
  IconButton,
  Container,
  useTheme,
  useMediaQuery,
  Fade,
  Chip,
  Alert,
  Stack,
  alpha,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import PageTransition from "./components/PageTransition";
import { ACCENT, ACCENT_SOFT, SERIF, MEASURE } from "./data/studioStory";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendIcon from "@mui/icons-material/Send";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import StopIcon from "@mui/icons-material/Stop";
import InfoIcon from "@mui/icons-material/Info";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import "./index.css";

// Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || "https://chatddk-backend.onrender.com";

function App() {
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const chatContainerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const messageIdCounter = useRef(0);

  // Scroll to bottom when chatHistory updates (only if user hasn't scrolled up)
  useEffect(() => {
    if (chatContainerRef.current && !userHasScrolledUp) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory, userHasScrolledUp]);

  // Detect when user manually scrolls
  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = chatContainer;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      
      // If user is more than 100px from bottom, they've scrolled up
      const isNearBottom = distanceFromBottom < 100;
      setUserHasScrolledUp(!isNearBottom);
    };

    chatContainer.addEventListener('scroll', handleScroll);
    return () => chatContainer.removeEventListener('scroll', handleScroll);
  }, []);

  // Generate unique message ID
  const generateMessageId = () => {
    return `msg-${Date.now()}-${++messageIdCounter.current}`;
  };

  // Handle stop streaming
  const handleStopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    setIsLoading(false);
  };

  // overrideText lets a conversation starter send its own text. It cannot read
  // `message` instead: setMessage is async, so a starter that set state and then
  // called this would hit the stale closure and silently send nothing.
  // Note the guard — this is also wired straight to onClick, which passes an event.
  const handleSendMessage = async (overrideText) => {
    const text = typeof overrideText === "string" ? overrideText : message;
    if (!text.trim()) return;

    const userMessageId = generateMessageId();
    const assistantMessageId = generateMessageId();

    setChatHistory((prev) => [...prev, {
      id: userMessageId,
      role: "user",
      content: text
    }]);
    setMessage("");
    setIsLoading(true);
    setError(null);
    setUserHasScrolledUp(false); // Reset scroll state - user wants to see the response

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    // Don't create streaming message yet - wait until content starts arriving
    let hasStartedStreaming = false;

    try {
      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: text,
          sessionId: sessionId
        }),
        signal: abortControllerRef.current.signal,
      });

      // Check if the request was rate limited
      if (response.status === 429) {
        setChatHistory((prev) => [
          ...prev,
          {
            id: assistantMessageId,
            role: "assistant",
            content: "⚠️ **Daily Message Limit Reached**\n\nYou've reached the maximum of 10 messages per day. This limit helps protect the service from abuse.\n\nPlease try again tomorrow! Your limit will reset in 24 hours from your first message today.",
          },
        ]);
        setIsLoading(false);
        return;
      }

      // Check for other HTTP errors
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let streamedContent = "";

      setIsStreaming(true);

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        // Check if request was aborted
        if (abortControllerRef.current?.signal.aborted) {
          break;
        }

        // Decode the chunk
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.error) {
                throw new Error(data.error);
              }

              // Handle session ID
              if (data.type === 'session' && data.sessionId) {
                setSessionId(data.sessionId);
              }
              
              if (data.content && data.type === 'content') {
                streamedContent += data.content;
                
                // Turn off loading state and create streaming message on first content
                if (!hasStartedStreaming) {
                  setIsLoading(false);
                  hasStartedStreaming = true;
                  
                  // Create the streaming message now
                  setChatHistory((prev) => [
                    ...prev, 
                    { 
                      id: assistantMessageId,
                      role: "assistant", 
                      content: streamedContent, 
                      isStreaming: true 
                    }
                  ]);
                } else {
                  // Update the existing streaming message
                  setChatHistory((prev) => {
                    const newHistory = [...prev];
                    const lastMessageIndex = newHistory.findIndex(msg => msg.id === assistantMessageId);
                    if (lastMessageIndex !== -1) {
                      newHistory[lastMessageIndex] = {
                        ...newHistory[lastMessageIndex],
                        content: streamedContent
                      };
                    }
                    return newHistory;
                  });
                }
              }
              
              if (data.done && data.type === 'content') {
                // Mark streaming as complete
                setChatHistory((prev) => {
                  const newHistory = [...prev];
                  const lastMessageIndex = newHistory.findIndex(msg => msg.id === assistantMessageId);
                  if (lastMessageIndex !== -1) {
                    newHistory[lastMessageIndex] = {
                      ...newHistory[lastMessageIndex],
                      isStreaming: false
                    };
                  }
                  return newHistory;
                });
                setIsStreaming(false);
                break;
              }
            } catch (parseError) {
              console.error("Error parsing streaming data:", parseError);
            }
          }
        }
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log("Request aborted by user");
        // Remove partial streaming message if it exists
        if (hasStartedStreaming) {
          setChatHistory((prev) => prev.filter(msg => msg.id !== assistantMessageId));
        }
        return;
      }

      console.error("Error communicating with backend:", error);
      setError("Connection Error: Unable to connect to ChatDDK backend. Please check your internet connection and try again.");
      
      setChatHistory((prev) => {
        // If streaming started, remove the partial message; otherwise just add error
        const filteredHistory = hasStartedStreaming ? 
          prev.filter(msg => msg.id !== assistantMessageId) : prev;
        return [
          ...filteredHistory,
          {
            id: assistantMessageId,
            role: "assistant",
            content: "❌ **Connection Error**\n\nUnable to connect to ChatDDK backend. Please check your internet connection and try again.",
          },
        ];
      });
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  // Clearing mints a NEW server session, so ChatDDK genuinely forgets the
  // conversation — there is no undo to offer. With a 10-message daily cap,
  // losing the thread mid-conversation is expensive, so it asks first.
  const handleClearChat = async () => {
    setConfirmClearOpen(false);

    // Stop any ongoing streaming
    if (isStreaming) {
      handleStopStreaming();
    }

    try {
      const response = await fetch(`${API_BASE_URL}/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId }),
      });

      if (response.ok) {
        const data = await response.json();
        setChatHistory([]);
        setSessionId(data.sessionId);
        setError(null);
        console.log("Conversation reset successfully.");
      } else {
        console.error("Failed to reset conversation.");
        setError("Failed to reset conversation. Please try again.");
      }
    } catch (error) {
      console.error("Error resetting conversation:", error);
      setError("Error resetting conversation. Please try again.");
    }
  };

  // Handle Enter key for mobile
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (isMobile || !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    }
  };

  return (
    <PageTransition>
      <Helmet>
        <title>Chat with ChatDDK – David Kooi</title>
        <link rel="canonical" href="https://davidkooi.com/chat/" />
        <meta name="description" content="Chat with ChatDDK, David Kooi's AI persona. Ask questions about entrepreneurship, AI, product strategy, and more." />
      </Helmet>
      
      <Box sx={{ minHeight: "100vh", backgroundColor: theme.palette.background.default }}>
        <Navigation />
      
      {/* Same ground and warm radial wash as the studio surfaces. This page used
          to be a violet SaaS gradient while its own nav and footer were navy —
          ChatDDK read as a different product bolted onto the site. */}
      <Box
        sx={{
          minHeight: "calc(100vh - 64px)",
          background: `radial-gradient(1100px 480px at 50% -8%, ${alpha(
            ACCENT,
            theme.palette.mode === "dark" ? 0.12 : 0.09
          )}, transparent 62%), ${theme.palette.background.default}`,
          display: "flex",
          flexDirection: "column",
          py: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="lg" sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              textAlign: "center",
              mb: 4,
              animation: "fadeInDown 0.8s ease-out",
            }}
          >
            <Typography
              variant={isMobile ? "h4" : "h3"}
              component="h1"
              sx={{
                fontFamily: SERIF,
                fontWeight: 600,
                letterSpacing: "-0.01em",
                color: "text.primary",
                mb: 1,
              }}
            >
              ChatDDK
            </Typography>
            <Typography
              variant="h6"
              component="p"
              sx={{
                color: "text.secondary",
                fontWeight: 400,
              }}
            >
              A digital approximation of David Kooi
            </Typography>

          {/* Info Section */}
          <Box sx={{ mt: 3 }}>
            <IconButton
              onClick={() => setInfoOpen(!infoOpen)}
              aria-label={infoOpen ? "Hide what ChatDDK knows" : "What ChatDDK knows and its limits"}
              aria-expanded={infoOpen}
              sx={{
                width: 44,
                height: 44,
                color: "text.secondary",
                border: `1px solid ${theme.palette.divider}`,
                "&:hover": {
                  borderColor: ACCENT,
                  color: "accent.text",
                },
              }}
            >
              <InfoIcon />
            </IconButton>
            <Collapse in={infoOpen}>
              <Box
                sx={{
                  mt: 2,
                  p: 3,
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.02)
                    : theme.palette.common.white,
                  borderRadius: 3,
                  textAlign: "left",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="body2" paragraph sx={{ color: 'text.primary', maxWidth: '68ch' }}>
                  <strong>Welcome to ChatDDK!</strong> This AI assistant is trained on David Kooi's 
                  writings, experiences, and philosophy. Ask about entrepreneurship, AI, product strategy, 
                  or any topic that interests you.
                </Typography>
                <Typography variant="body2" paragraph sx={{ color: 'text.secondary', maxWidth: '68ch' }}>
                  <strong>Important Note:</strong> ChatDDK does not have access to any confidential information about David Kooi's projects, employers, family, and friends. If the assistant provides any information that appears to be confidential, it should be considered a hallucination.
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                  <Chip 
                    label="10 messages/day" 
                    size="small" 
                    sx={{ 
                      color: 'accent.text',
                      backgroundColor: ACCENT_SOFT
                    }} 
                  />
                  <Chip 
                    label="Streaming responses" 
                    size="small" 
                    sx={{ 
                      color: 'accent.text',
                      backgroundColor: ACCENT_SOFT
                    }} 
                  />
                  <Chip 
                    label="Session-based" 
                    size="small" 
                    sx={{ 
                      color: 'accent.text',
                      backgroundColor: ACCENT_SOFT
                    }} 
                  />
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              mb: 2,
              // MUI's default close control is an IconButton size="small" —
              // ~30x30, under the 44px touch floor. Targeted through the class
              // rather than slotProps so it does not depend on which slot name
              // this MUI major happens to use. Alert's action slot defaults to
              // flex-start, which would leave a 44px button top-aligned against
              // a one-line message.
              "& .MuiAlert-action": { alignItems: "center", pt: 0 },
              "& .MuiAlert-action .MuiIconButton-root": {
                minWidth: 44,
                minHeight: 44,
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Chat Container */}
        <Paper
          // elevation 0: MUI's dark-mode elevation overlay paints a lightening
          // gradient over the fill, which turned this card into a grey slab.
          elevation={0}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            borderRadius: 4,
            overflow: "hidden",
            // Same card treatment as the studio surfaces: near-transparent fill
            // over the page ground plus a hairline border, not a grey slab.
            backgroundColor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.common.white, 0.02)
              : theme.palette.common.white,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.palette.mode === 'dark'
              ? "0 20px 60px rgba(0,0,0,0.5)"
              : "0 20px 60px rgba(0,0,0,0.08)",
            position: "relative", // For absolute positioning of scroll button
          }}
        >
          {/* Messages Area */}
          <Box
            ref={chatContainerRef}
            sx={{
              flex: 1,
              p: { xs: 2, md: 3 },
              overflowY: "auto",
              minHeight: { xs: "50vh", md: "60vh" },
              maxHeight: { xs: "60vh", md: "65vh" },
              // Custom scrollbar
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(0,0,0,0.05)",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(0,0,0,0.2)",
                borderRadius: "4px",
                "&:hover": {
                  background: "rgba(0,0,0,0.3)",
                },
              },
            }}
          >
            {chatHistory.length === 0 && !isLoading && (
              <Box
                className="welcome-section"
                sx={{
                  textAlign: "center",
                  py: 6,
                  px: 2,
                  backgroundColor: "transparent",
                }}
              >
                <Typography
                  variant="h6"
                  component="h2"
                  gutterBottom
                  sx={{
                    color: "text.primary",
                    fontWeight: 600,
                  }}
                >
                  Hello, I'm ChatDDK.
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mb: 4,
                    maxWidth: MEASURE,
                    mx: "auto",
                  }}
                >
                  David Kooi's brain in a chat box. Ask away and let's see where the rabbit hole leads.
                </Typography>
                
                {/* Conversation Starters */}
                <Box sx={{ maxWidth: 500, mx: "auto" }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: "text.primary", 
                      mb: 3, 
                      fontWeight: 600,
                    }}
                  >
                    Try asking:
                  </Typography>
                  <Stack spacing={2}>
                    {[
                      "What do you want to be when you grow up?",
                      "Tell me about your history with endurance sports?",
                      "What is your philosophy of life?"
                    ].map((starter, index) => (
                      <Button
                        key={index}
                        variant="outlined"
                        onClick={() => handleSendMessage(starter)}
                        sx={{
                          borderRadius: 3,
                          borderColor: theme.palette.divider,
                          color: "text.primary",
                          textTransform: "none",
                          py: 2,
                          px: 3,
                          fontSize: "0.95rem",
                          fontWeight: 500,
                          textAlign: "left",
                          justifyContent: "flex-start",
                          minHeight: "48px",
                          "&:hover": {
                            borderColor: ACCENT,
                            backgroundColor: alpha(ACCENT, 0.08),
                            transform: "translateY(-2px)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        {starter}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              </Box>
            )}

            {chatHistory.map((entry) => (
              <Fade in={true} key={entry.id} timeout={600}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: entry.role === "user" ? "flex-end" : "flex-start",
                    alignItems: "flex-start",
                    mb: 2,
                    gap: 1.5,
                  }}
                >
                  {/* Avatar for assistant messages */}
                  {entry.role === "assistant" && (
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background: ACCENT,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        boxShadow: `0 2px 8px ${alpha(ACCENT, 0.3)}`,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "accent.contrastText",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        DDK
                      </Typography>
                    </Box>
                  )}
                  <Paper
                    elevation={entry.role === "user" ? 8 : 4}
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      maxWidth: { xs: "85%", md: "75%" },
                      background: entry.role === "user" 
                        ? ACCENT
                        : theme.palette.background.paper,
                      color: entry.role === "user" ? "accent.contrastText" : "text.primary",
                      borderRadius: entry.role === "user" 
                        ? "20px 20px 4px 20px" 
                        : "20px 20px 20px 4px",
                      boxShadow: entry.role === "user"
                        ? `0 8px 25px ${alpha(ACCENT, 0.4)}`
                        : theme.palette.mode === 'dark' 
                          ? "0 4px 15px rgba(0,0,0,0.3)"
                          : "0 4px 15px rgba(0,0,0,0.1)",
                      transform: "translateY(0)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: entry.role === "user"
                          ? `0 12px 30px ${alpha(ACCENT, 0.5)}`
                          : theme.palette.mode === 'dark'
                            ? "0 8px 20px rgba(0,0,0,0.4)"
                            : "0 8px 20px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        lineHeight: 1.6,
                        color: entry.role === "user" ? "accent.contrastText" : "text.primary",
                        "& p": { margin: 0, color: entry.role === "user" ? "accent.contrastText" : "text.primary" },
                        "& p:not(:last-child)": { mb: 1 },
                        "& strong": { color: entry.role === "user" ? "accent.contrastText" : "text.primary" },
                        "& *": { color: entry.role === "user" ? "accent.contrastText" : "text.primary" },
                      }}
                    >
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeSanitize]}
                        components={{
                          // Prevent any script or dangerous HTML elements
                          script: () => null,
                          iframe: () => null,
                          object: () => null,
                          embed: () => null,
                        }}
                      >
                        {entry.content}
                      </ReactMarkdown>
                      {entry.isStreaming && (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            width: "8px",
                            height: "20px",
                            backgroundColor: ACCENT,
                            marginLeft: "2px",
                            animation: "blink 1s infinite",
                            "@keyframes blink": {
                              "0%, 50%": { opacity: 1 },
                              "51%, 100%": { opacity: 0 },
                            },
                          }}
                        />
                      )}
                    </Typography>
                  </Paper>
                </Box>
              </Fade>
            ))}

            {isLoading && (
              <Fade in={true}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    mb: 2,
                    gap: 1.5,
                  }}
                >
                  {/* Avatar for loading state */}
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: ACCENT,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: `0 2px 8px ${alpha(ACCENT, 0.3)}`,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "accent.contrastText",
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        letterSpacing: "-0.5px",
                      }}
                    >
                      DDK
                    </Typography>
                  </Box>
                  <Paper
                    elevation={4}
                    sx={{
                      p: 2.5,
                      borderRadius: "20px 20px 20px 4px",
                      backgroundColor: theme.palette.background.paper,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {/* Bouncing dots */}
                    {[0, 1, 2].map((index) => (
                      <Box
                        key={index}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: ACCENT,
                          animation: "bounce 1.4s ease-in-out infinite",
                          animationDelay: `${index * 0.16}s`,
                          "@keyframes bounce": {
                            "0%, 80%, 100%": {
                              transform: "translateY(0)",
                            },
                            "40%": {
                              transform: "translateY(-8px)",
                            },
                          },
                        }}
                      />
                    ))}
                  </Paper>
                </Box>
              </Fade>
            )}
          </Box>

          {/* Scroll to Bottom Button */}
          {userHasScrolledUp && isStreaming && (
            <Fade in={true}>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 140,
                  right: 30,
                  zIndex: 10,
                }}
              >
                <IconButton
                  onClick={() => {
                    if (chatContainerRef.current) {
                      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                      setUserHasScrolledUp(false);
                    }
                  }}
                  sx={{
                    background: ACCENT,
                    color: "accent.contrastText",
                    boxShadow: `0 4px 12px ${alpha(ACCENT, 0.5)}`,
                    "&:hover": {
                      background: "hsl(26 95% 44%)",
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <KeyboardArrowDownIcon />
                </IconButton>
              </Box>
            </Fade>
          )}

          {/* Input Area */}
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              borderTop: theme.palette.mode === 'dark'
                ? `1px solid ${alpha(theme.palette.common.white, 0.1)}`
                : "1px solid rgba(0,0,0,0.08)",
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                variant="outlined"
                placeholder="Ask anything."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading || isStreaming}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? alpha(theme.palette.background.paper, 0.5)
                      : "#f8fafc",
                    color: "text.primary",
                    "& fieldset": {
                      borderColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.2)
                        : "rgba(0,0,0,0.12)",
                    },
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "primary.main",
                      borderWidth: 2,
                    },
                    "& input": {
                      color: "text.primary",
                    },
                    "& textarea": {
                      color: "text.primary",
                    },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "text.secondary",
                    opacity: 1,
                  },
                }}
              />
              <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 1 }}>
                {isStreaming ? (
                  <Button
                    variant="outlined"
                    onClick={handleStopStreaming}
                    startIcon={<StopIcon />}
                    sx={{
                      minWidth: { xs: "100%", md: 120 },
                      height: 56,
                      borderRadius: 3,
                      color: "#d32f2f",
                      borderColor: "#d32f2f",
                      "&:hover": {
                        borderColor: "#b71c1c",
                        backgroundColor: "rgba(211, 47, 47, 0.04)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Stop
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleSendMessage}
                    disabled={!message.trim() || isLoading}
                    startIcon={<SendIcon />}
                    sx={{
                      minWidth: { xs: "100%", md: 120 },
                      height: 56,
                      borderRadius: 3,
                      background: ACCENT,
                      boxShadow: `0 4px 15px ${alpha(ACCENT, 0.4)}`,
                      "&:hover": {
                        background: "hsl(26 95% 44%)",
                        boxShadow: `0 6px 20px ${alpha(ACCENT, 0.6)}`,
                        transform: "translateY(-2px)",
                      },
                      "&:disabled": {
                        background: "rgba(0,0,0,0.12)",
                        boxShadow: "none",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Send
                  </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={() => setConfirmClearOpen(true)}
                  disabled={isLoading || isStreaming || chatHistory.length === 0}
                  startIcon={<ClearAllIcon />}
                  sx={{
                    minWidth: { xs: "100%", md: 120 },
                    height: 56,
                    borderRadius: 3,
                    borderColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.3)
                      : "rgba(0,0,0,0.23)",
                    color: "text.primary",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      color: "primary.main",
                    },
                    "&:disabled": {
                      color: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.3)
                        : "rgba(0,0,0,0.26)",
                      borderColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.12)
                        : "rgba(0,0,0,0.12)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Box>

            {/* Mobile-specific hint */}
            {isMobile && (
              <Typography 
                variant="caption" 
                sx={{ 
                  display: "block", 
                  textAlign: "center", 
                  mt: 1, 
                  color: "text.secondary"
                }}
              >
                Tap Enter to send • Use Send button for newlines
              </Typography>
            )}

            {/* Question-logging notice (David approved 2026-10-04). The backend
                records the question text only, with no IP address, user agent
                or name; see chatddk-backend/questionLog.js. Keep this sentence
                true to what that module sends. */}
            <Typography
              variant="caption"
              sx={{ display: "block", textAlign: "center", mt: 1, color: "text.secondary" }}
            >
              Questions you ask here are saved (without your IP address) so I can improve these answers.
            </Typography>
          </Box>
        </Paper>

        {/* Footer */}
        <Box
          sx={{
            textAlign: "center",
            mt: 3,
            color: "text.secondary",
          }}
        >
          {/* The session id used to print here. It means nothing to a visitor. */}
          <Typography variant="body2">
            Powered by Anthropic Claude • Built on David&rsquo;s writing and work
          </Typography>
        </Box>

        <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)}>
          <DialogTitle>Clear this conversation?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ChatDDK will forget everything you&rsquo;ve discussed and start fresh. The questions you asked stay saved, without your IP address.
              This can&rsquo;t be undone, and it doesn&rsquo;t give you back any of
              your 10 messages for today.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmClearOpen(false)}>Keep talking</Button>
            <Button onClick={handleClearChat} color="error" variant="contained">
              Clear conversation
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
      
    <Footer />
  </Box>
  </PageTransition>
  );
}

export default App;
